import React, { Component } from 'react';
import { connect } from 'react-redux';
import { getHealthCheckReport, getHealthChecks, showAlert, startHealthCheck } from 'actions/index';
import PropTypes from 'prop-types';
import HealthCheckDetailView from './HealthCheckDetailView';

const OLDER_HEALTH_CHECKS_LIMIT = 8;

function getReportId(report) {
  return report?.report_id ?? report?.id;
}

class HealthCheckDetailContainer extends Component {
  state = {
    railReportId: null,
    olderReportsAnchorKey: null,
  };

  componentDidMount() {
    const reportId = this.getReportIdFromRoute();
    if (reportId) {
      this.fetchSelectedReport(reportId);
    }

    const selectedReport = this.getSelectedReport();
    if (selectedReport) {
      this.syncRailForSelectedReport(selectedReport);
      this.fetchOlderReports(selectedReport);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const reportId = this.getReportIdFromRoute();
    const prevReportId = prevProps.match?.params?.reportId
      ? String(prevProps.match.params.reportId).trim() || null
      : null;
    if (reportId && reportId !== prevReportId) {
      this.fetchSelectedReport(reportId);
    }

    const selectedReport = this.getSelectedReport();
    if (selectedReport) {
      this.syncRailForSelectedReport(selectedReport);
      this.fetchOlderReports(selectedReport);
    }

    if (this.props.olderReports !== prevProps.olderReports) {
      this.syncRailFromOlderReports();
    }

    if (this.state.railReportId && this.state.railReportId !== prevState.railReportId) {
      this.fetchRailReport(this.state.railReportId);
    }
  }

  getReportIdFromRoute() {
    const { match } = this.props;
    const id = match?.params?.reportId;
    return id ? String(id).trim() || null : null;
  }

  getReportFromCache(reportId) {
    if (reportId == null || reportId === '') return null;
    return this.props.reportDetailsById[String(reportId)] || null;
  }

  getSelectedReport() {
    const reportId = this.getReportIdFromRoute();
    if (reportId) {
      return this.getReportFromCache(reportId);
    }

    return this.props.reportDetail;
  }

  fetchSelectedReport(reportId) {
    if (!this.getReportFromCache(reportId)) {
      this.props.fetchReport(reportId, { role: 'selected' });
    }
  }

  fetchRailReport(reportId) {
    if (!this.getReportFromCache(reportId)) {
      this.props.fetchReport(reportId, { role: 'rail' });
    }
  }

  syncRailForSelectedReport(report) {
    const reportId = getReportId(report);
    if (this.state.railReportId || reportId == null || reportId === '') return;

    this.setState({ railReportId: String(reportId) });
  }

  syncRailFromOlderReports() {
    const latestReport = (this.props.olderReports || []).find(
      report => getReportId(report) != null,
    );
    const latestReportId = getReportId(latestReport);

    if (latestReportId == null || latestReportId === '') return;

    const nextRailReportId = String(latestReportId);
    if (nextRailReportId !== this.state.railReportId) {
      this.setState({ railReportId: nextRailReportId });
    }
  }

  fetchOlderReports(report) {
    if (!report?.location_id) return;

    const anchorKey = `${report.location_id}:${report.device_type || ''}`;
    if (this.state.olderReportsAnchorKey === anchorKey) return;

    this.setState({ olderReportsAnchorKey: anchorKey });
    this.props.fetchOlderReports({
      limit: OLDER_HEALTH_CHECKS_LIMIT,
      offset: 0,
      location_id: report.location_id,
      device_type: report.device_type || undefined,
    });
  }

  render() {
    const {
      olderReports,
      olderReportsLoading,
      reportDetail,
      reportDetailsById,
      reportError,
      selectedReportLoading,
      selectedReportError,
      railReportLoading,
      location,
      showAlert,
      startHealthCheck,
      startHealthCheckLoading,
    } = this.props;
    const reportId = this.getReportIdFromRoute();
    const selectedReport = this.getSelectedReport();
    const railReport = this.state.railReportId
      ? reportDetailsById[String(this.state.railReportId)] || selectedReport
      : selectedReport;
    const displayReport = selectedReport || reportDetail;
    const loading = selectedReportLoading && !displayReport;
    const error = displayReport ? null : selectedReportError || reportError;

    return (
      <HealthCheckDetailView
        report={displayReport}
        railReport={railReport}
        loading={loading}
        selectedReportLoading={selectedReportLoading}
        railReportLoading={railReportLoading}
        error={error}
        location={location}
        olderReports={olderReports}
        olderReportsLoading={olderReportsLoading}
        reportId={reportId}
        showAlert={showAlert}
        startHealthCheck={startHealthCheck}
        startHealthCheckLoading={startHealthCheckLoading}
      />
    );
  }
}

HealthCheckDetailContainer.propTypes = {
  fetchOlderReports: PropTypes.func.isRequired,
  fetchReport: PropTypes.func.isRequired,
  location: PropTypes.object,
  match: PropTypes.shape({ params: PropTypes.object }).isRequired,
  olderReports: PropTypes.array.isRequired,
  olderReportsLoading: PropTypes.bool.isRequired,
  railReportLoading: PropTypes.bool.isRequired,
  reportDetail: PropTypes.object,
  reportDetailsById: PropTypes.object.isRequired,
  reportError: PropTypes.string,
  selectedReportError: PropTypes.string,
  selectedReportLoading: PropTypes.bool.isRequired,
  showAlert: PropTypes.func.isRequired,
  startHealthCheck: PropTypes.func.isRequired,
  startHealthCheckLoading: PropTypes.bool.isRequired,
};

function mapStateToProps(state) {
  return {
    olderReports: state.healthCheck.list || [],
    olderReportsLoading: state.healthCheck.loading,
    reportDetail: state.healthCheck.reportDetail,
    reportDetailsById: state.healthCheck.reportDetailsById || {},
    reportError: state.healthCheck.reportError,
    railReportLoading: state.healthCheck.railReportLoading,
    selectedReportError: state.healthCheck.selectedReportError,
    selectedReportLoading: state.healthCheck.selectedReportLoading,
    startHealthCheckLoading: state.healthCheck.startHealthCheckLoading,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    fetchOlderReports: params => dispatch(getHealthChecks(params)),
    fetchReport: (reportId, meta) => dispatch(getHealthCheckReport(reportId, meta)),
    showAlert: (message, options) => dispatch(showAlert(message, options)),
    startHealthCheck: params => dispatch(startHealthCheck(params)),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(HealthCheckDetailContainer);
